/**
 * Editorial Reveal — Scroll-Linked Word Reveal
 *
 * Rich text section where words transition from dimmed to
 * full opacity, scrubbed to scroll position via GSAP ScrollTrigger.
 * Highlighted words (strong/em) transition to an accent
 * colour as they reveal.
 *
 * Desktop: word-by-word scrub reveal
 * Mobile: block-level fade-up on scroll entry
 *
 * Requires GSAP + ScrollTrigger (loaded by the section).
 */
(function () {
  'use strict';

  function waitForDeps(cb) {
    if (window.gsap && window.ScrollTrigger) return cb();
    var check = setInterval(function () {
      if (window.gsap && window.ScrollTrigger) {
        clearInterval(check);
        cb();
      }
    }, 50);
    setTimeout(function () { clearInterval(check); }, 8000);
  }

  /* ══════════════════════════════════════════
   *  WORD SPLITTING — preserves inline HTML
   *
   *  Walks text nodes via TreeWalker, splits on
   *  whitespace, wraps each word in <span class="er-word">.
   *  Words inside <strong>/<em>/<b>/<i> also get
   *  .er-word--highlight for the colour shift.
   * ══════════════════════════════════════════ */

  function splitWords(container) {
    var walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    var textNodes = [];
    var node;
    while (node = walker.nextNode()) {
      if (node.nodeValue.trim().length > 0) {
        textNodes.push(node);
      }
    }

    textNodes.forEach(function (textNode) {
      var parent = textNode.parentNode;
      var words = textNode.nodeValue.split(/(\s+)/);
      var fragment = document.createDocumentFragment();

      /* Check if parent is a highlight tag */
      var tag = parent.tagName ? parent.tagName.toLowerCase() : '';
      var isHighlight = (tag === 'strong' || tag === 'b' || tag === 'em' || tag === 'i');

      words.forEach(function (word) {
        if (/^\s+$/.test(word)) {
          /* Keep whitespace as text node for natural wrapping */
          fragment.appendChild(document.createTextNode(word));
        } else if (word.length > 0) {
          var span = document.createElement('span');
          span.className = 'er-word';
          span.textContent = word;
          if (isHighlight) {
            span.classList.add('er-word--highlight');
          }
          fragment.appendChild(span);
        }
      });

      parent.replaceChild(fragment, textNode);
    });
  }

  /* ══════════════════════════════════════════
   *  INIT SECTION
   * ══════════════════════════════════════════ */

  function initEditorialReveal(sectionId) {
    var section = document.querySelector(
      '[data-editorial-reveal-id="' + sectionId + '"]'
    );
    if (!section) return;

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    gsap.registerPlugin(ScrollTrigger);

    var triggers = [];

    /* ── Reduced motion: show everything immediately ── */
    if (reducedMotion) {
      section.querySelectorAll('[data-er-reveal="words"]').forEach(function (block) {
        block.style.opacity = '1';
        var highlights = block.querySelectorAll('strong, em, b, i');
        highlights.forEach(function (el) {
          el.classList.add('er-highlight--mobile');
        });
      });
      section._editorialRevealCleanup = function () {};
      return;
    }

    triggers = initDesktopReveal(section);

    /* ── Cleanup for Shopify editor ── */
    section._editorialRevealCleanup = function () {
      triggers.forEach(function (st) { st.kill(); });
      triggers = [];
    };
  }

  /* ══════════════════════════════════════════
   *  DESKTOP — word-by-word scrub reveal
   * ══════════════════════════════════════════ */

  function initDesktopReveal(section) {
    var revealBlocks = section.querySelectorAll('[data-er-reveal="words"]');
    var allWords = [];
    var highlightWords = [];

    revealBlocks.forEach(function (block) {
      splitWords(block);
      var words = block.querySelectorAll('.er-word');
      for (var i = 0; i < words.length; i++) {
        allWords.push(words[i]);
        if (words[i].classList.contains('er-word--highlight')) {
          highlightWords.push(words[i]);
        }
      }
    });

    if (allWords.length === 0) return [];

    var dimmedOpacity = (parseFloat(section.dataset.dimmedOpacity) || 15) / 100;
    var highlightColor = section.dataset.highlightColor || '#c8a97e';

    /* Set initial dimmed state */
    gsap.set(allWords, { opacity: dimmedOpacity });

    /* ── Master timeline scrubbed to section scroll ── */
    var total = allWords.length;
    var overlapFactor = 3;
    var wordDuration = overlapFactor / total;

    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top 80%',
        end: 'bottom 55%',
        scrub: 0.5
      }
    });

    /* Word opacity reveal */
    allWords.forEach(function (word, idx) {
      var startPos = idx / total;
      tl.fromTo(word,
        { opacity: dimmedOpacity },
        { opacity: 1, duration: wordDuration, ease: 'none' },
        startPos
      );
    });

    /* Highlighted words: shift to accent colour */
    highlightWords.forEach(function (word) {
      var idx = allWords.indexOf(word);
      var startPos = idx / total;
      tl.fromTo(word,
        { color: 'inherit' },
        { color: highlightColor, duration: wordDuration, ease: 'none' },
        startPos
      );
    });

    return [tl.scrollTrigger];
  }

  /* ══════════════════════════════════════════
   *  BOOT + SHOPIFY EDITOR EVENTS
   * ══════════════════════════════════════════ */

  function boot() {
    document.querySelectorAll('[data-editorial-reveal-id]').forEach(function (el) {
      var id = el.dataset.editorialRevealId;
      if (!el._editorialRevealInit) {
        el._editorialRevealInit = true;
        waitForDeps(function () { initEditorialReveal(id); });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-editorial-reveal-id]');
    if (el) {
      el._editorialRevealInit = false;
      waitForDeps(function () {
        initEditorialReveal(el.dataset.editorialRevealId);
        ScrollTrigger.refresh();
      });
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('[data-editorial-reveal-id]');
    if (el && el._editorialRevealCleanup) el._editorialRevealCleanup();
  });
})();
